import { useState, useMemo, useEffect, useCallback, useRef, TouchEvent } from "react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { ChevronLeft, ChevronRight, Clock, User, Mail, Euro, ExternalLink, CalendarPlus, BanIcon, Pencil, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";

import {
  format,
  startOfMonth,
  endOfMonth,
  parseISO,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  isSameMonth,
  isSameDay,
  isToday,
  getHours,
  getMinutes,
  setMonth,
  setYear,
  getMonth,
  getYear,
} from "date-fns";

type ViewMode = "month" | "week" | "day";

const useSwipe = (onSwipeLeft: () => void, onSwipeRight: () => void, threshold = 50) => {
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const onTouchStart = useCallback((e: TouchEvent) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, []);

  const onTouchEnd = useCallback((e: TouchEvent) => {
    if (!touchStart.current) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    const dy = e.changedTouches[0].clientY - touchStart.current.y;
    // Only trigger if horizontal swipe is dominant
    if (Math.abs(dx) > threshold && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) onSwipeLeft();
      else onSwipeRight();
    }
    touchStart.current = null;
  }, [onSwipeLeft, onSwipeRight, threshold]);

  return { onTouchStart, onTouchEnd };
};

interface CalendarReservation {
  id: string;
  customerName: string;
  customerEmail: string;
  service: string;
  selectedOptions: string[];
  date: Date;
  duration: number;
  price: number;
  color: string;
}

interface BlockedTimeEntry {
  id: string;
  start_date: string;
  end_date: string | null;
  start_time: string | null;
  end_time: string | null;
  type: string;
}

interface OpeningHourEntry {
  day_of_week: number;
  is_open: boolean;
  open_time: string;
  close_time: string;
}

interface SlotClickPayload {
  date: Date;
  hour?: number;
  minute?: number;
  anchorEl?: HTMLElement;
}

const colorPalette = [
  "bg-primary/15 text-primary border-primary/30",
  "bg-accent/15 text-accent border-accent/30",
  "bg-destructive/15 text-destructive border-destructive/30",
];

/** Check if a given date falls within a blocked time entry */
const isDateBlocked = (date: Date, bt: BlockedTimeEntry): boolean => {
  const dateStr = format(date, "yyyy-MM-dd");
  if (bt.type === "single_day" || bt.type === "time_range") {
    return dateStr === bt.start_date;
  }
  return dateStr >= bt.start_date && (!bt.end_date || dateStr <= bt.end_date);
};

/** For week/day views: check if a specific time slot is blocked */
const isSlotBlocked = (date: Date, slotHour: number, slotMinute: number, blockedTimes: BlockedTimeEntry[]): boolean => {
  const dateStr = format(date, "yyyy-MM-dd");
  const slotTime = `${String(slotHour).padStart(2, "0")}:${String(slotMinute).padStart(2, "0")}:00`;

  return blockedTimes.some((bt) => {
    if (!isDateBlocked(date, bt)) return false;
    if (!bt.start_time && !bt.end_time) return true;
    if (bt.start_time && bt.end_time) {
      return slotTime >= bt.start_time && slotTime < bt.end_time;
    }
    return false;
  });
};

/** Check if any part of a day is blocked */
const isDayBlocked = (date: Date, blockedTimes: BlockedTimeEntry[]): boolean => {
  return blockedTimes.some((bt) => isDateBlocked(date, bt));
};

/** Find the first blocked time entry that applies to a date */
const findBlockedTimeForDate = (date: Date, blockedTimes: BlockedTimeEntry[]): BlockedTimeEntry | undefined => {
  return blockedTimes.find((bt) => isDateBlocked(date, bt));
};

/** Find the first blocked time entry that applies to a specific slot */
const findBlockedTimeForSlot = (date: Date, slotHour: number, slotMinute: number, blockedTimes: BlockedTimeEntry[]): BlockedTimeEntry | undefined => {
  const dateStr = format(date, "yyyy-MM-dd");
  const slotTime = `${String(slotHour).padStart(2, "0")}:${String(slotMinute).padStart(2, "0")}:00`;
  return blockedTimes.find((bt) => {
    if (!isDateBlocked(date, bt)) return false;
    if (!bt.start_time && !bt.end_time) return true;
    if (bt.start_time && bt.end_time) return slotTime >= bt.start_time && slotTime < bt.end_time;
    return false;
  });
};

const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const DatePickerPopover = ({ viewMode, currentDate, onDateChange, headerLabel }: { viewMode: ViewMode; currentDate: Date; onDateChange: (d: Date) => void; headerLabel: string }) => {
  const [open, setOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(getYear(currentDate));

  useEffect(() => {
    if (open) setPickerYear(getYear(currentDate));
  }, [open, currentDate]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="min-w-[140px] sm:min-w-[180px] text-center text-xs sm:text-sm font-medium text-foreground hover:bg-muted rounded-md px-2 py-1 transition-colors cursor-pointer">
          {headerLabel}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="center">
        {viewMode === "month" ? (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPickerYear(y => y - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-semibold text-foreground">{pickerYear}</span>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPickerYear(y => y + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {months.map((m, i) => (
                <button
                  key={m}
                  onClick={() => { onDateChange(setMonth(setYear(new Date(), pickerYear), i)); setOpen(false); }}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    getMonth(currentDate) === i && getYear(currentDate) === pickerYear
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground hover:bg-muted"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        ) : viewMode === "week" ? (
          <CalendarPicker
            mode="single"
            selected={currentDate}
            onSelect={(d) => {
              if (d) {
                onDateChange(startOfWeek(d, { weekStartsOn: 1 }));
                setOpen(false);
              }
            }}
            weekStartsOn={1}
            showWeekNumber
            className="p-3 pointer-events-auto"
            initialFocus
          />
        ) : (
          <CalendarPicker
            mode="single"
            selected={currentDate}
            onSelect={(d) => {
              if (d) { onDateChange(d); setOpen(false); }
            }}
            weekStartsOn={1}
            className="p-3 pointer-events-auto"
            initialFocus
          />
        )}
      </PopoverContent>
    </Popover>
  );
};

// Quick-action popover when clicking an empty slot
const SlotActionPopover = ({
  payload,
  onClose,
  onNewReservation,
  onBlockTime,
}: {
  payload: SlotClickPayload | null;
  onClose: () => void;
  onNewReservation: (date: Date, hour?: number, minute?: number) => void;
  onBlockTime: (date: Date, hour?: number, minute?: number) => void;
}) => {
  if (!payload) return null;
  const hasTime = payload.hour !== undefined && payload.minute !== undefined;
  const label = hasTime
    ? `${format(payload.date, "EEE d MMM")} · ${String(payload.hour).padStart(2, "0")}:${String(payload.minute).padStart(2, "0")}`
    : format(payload.date, "EEEE d MMMM");

  return (
    <Dialog open={!!payload} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-[300px]">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">{label}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2 pt-1">
          <Button
            className="justify-start gap-3 h-11"
            onClick={() => { onNewReservation(payload.date, payload.hour, payload.minute); onClose(); }}
          >
            <CalendarPlus className="h-4 w-4 shrink-0" />
            New reservation
          </Button>
          <Button
            variant="outline"
            className="justify-start gap-3 h-11"
            onClick={() => { onBlockTime(payload.date, payload.hour, payload.minute); onClose(); }}
          >
            <BanIcon className="h-4 w-4 shrink-0 text-destructive" />
            <span className="text-destructive">Block this {hasTime ? "timeslot" : "day"}</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CalendarPage = () => {
  const isMobile = useIsMobile();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>(
    typeof window !== "undefined" && window.innerWidth < 768 ? "day" : "month"
  );
  const [reservations, setReservations] = useState<CalendarReservation[]>([]);
  const [blockedTimes, setBlockedTimes] = useState<BlockedTimeEntry[]>([]);
  const [openingHours, setOpeningHours] = useState<OpeningHourEntry[]>([]);
  const [selectedReservation, setSelectedReservation] = useState<CalendarReservation | null>(null);
  const [slotAction, setSlotAction] = useState<SlotClickPayload | null>(null);
  const [selectedBlocked, setSelectedBlocked] = useState<BlockedTimeEntry | null>(null);
  const [confirmDeleteBlocked, setConfirmDeleteBlocked] = useState(false);
  const { profile } = useAuth();
  const navigateTo = useNavigate();
  const { t } = useLanguage();

  const viewModeLabels: Record<ViewMode, string> = {
    month: t("calendar.month"),
    week: t("calendar.week"),
    day: t("calendar.day"),
  };

  const fetchReservations = useCallback(async () => {
    if (!profile?.organisation_id) return;
    const { data } = await supabase
      .from("reservations")
      .select("id, date, duration, price, selected_options, customer:customers(name, email), service:services(name, service_options(id, name))")
      .eq("organisation_id", profile.organisation_id)
      .neq("status", "cancelled");

    if (data) {
      setReservations(
        data.map((r: any, i: number) => {
          const allOptions: { id: string; name: string }[] = r.service?.service_options || [];
          const selectedIds: string[] = r.selected_options || [];
          const optionNames = allOptions.filter((o: any) => selectedIds.includes(o.id)).map((o: any) => o.name);
          return {
            id: r.id,
            customerName: r.customer?.name || "Unknown",
            customerEmail: r.customer?.email || "",
            service: r.service?.name || "Unknown",
            selectedOptions: optionNames,
            date: new Date(r.date),
            duration: r.duration,
            price: Number(r.price) || 0,
            color: colorPalette[i % colorPalette.length],
          };
        })
      );
    }
  }, [profile?.organisation_id]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  const fetchBlockedTimes = useCallback(async () => {
    if (!profile?.organisation_id) return;
    const { data } = await supabase
      .from("blocked_times")
      .select("id, start_date, end_date, start_time, end_time, type")
      .eq("organisation_id", profile.organisation_id)
      .eq("active", true);
    if (data) setBlockedTimes(data);
  }, [profile?.organisation_id]);

  useEffect(() => {
    fetchBlockedTimes();
  }, [fetchBlockedTimes]);

  const fetchOpeningHours = useCallback(async () => {
    if (!profile?.organisation_id) return;
    const { data } = await supabase
      .from("opening_hours")
      .select("day_of_week, is_open, open_time, close_time")
      .eq("organisation_id", profile.organisation_id);
    if (data) setOpeningHours(data);
  }, [profile?.organisation_id]);

  useEffect(() => {
    fetchOpeningHours();
  }, [fetchOpeningHours]);

  useEffect(() => {
    if (!profile?.organisation_id) return;

    const channel = supabase
      .channel('calendar-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'reservations', filter: `organisation_id=eq.${profile.organisation_id}` }, () => fetchReservations())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'blocked_times', filter: `organisation_id=eq.${profile.organisation_id}` }, () => fetchBlockedTimes())
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.organisation_id, fetchReservations, fetchBlockedTimes]);

  const navigateCalendar = useCallback((direction: "prev" | "next") => {
    if (viewMode === "month") {
      setCurrentDate(direction === "next" ? addMonths(currentDate, 1) : subMonths(currentDate, 1));
    } else if (viewMode === "week") {
      setCurrentDate(direction === "next" ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1));
    } else {
      setCurrentDate(direction === "next" ? addDays(currentDate, 1) : addDays(currentDate, -1));
    }
  }, [viewMode, currentDate]);

  const swipeHandlers = useSwipe(
    () => navigateCalendar("next"),
    () => navigateCalendar("prev")
  );

  const headerLabel = useMemo(() => {
    if (viewMode === "month") return format(currentDate, "MMMM yyyy");
    if (viewMode === "week") {
      const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
      const we = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(ws, "MMM d")} – ${format(we, "MMM d, yyyy")}`;
    }
    return format(currentDate, "EEEE, MMMM d, yyyy");
  }, [currentDate, viewMode]);

  // Navigate to create reservation page with prefilled date/time
  const handleNewReservation = (date: Date, hour?: number, minute?: number) => {
    const params = new URLSearchParams({ date: format(date, "yyyy-MM-dd") });
    if (hour !== undefined && minute !== undefined)
      params.set("time", `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`);
    navigateTo(`/dashboard/reservations/new?${params.toString()}`);
  };

  const handleBlockTime = (date: Date, hour?: number, minute?: number) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const params = new URLSearchParams({ date: dateStr });
    if (hour !== undefined && minute !== undefined) {
      params.set("start_time", `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`);
      const endMin = minute + 30;
      const endH = endMin >= 60 ? hour + 1 : hour;
      params.set("end_time", `${String(endH).padStart(2, "0")}:${String(endMin % 60).padStart(2, "0")}`);
    }
    navigateTo(`/dashboard/blocked/new?${params.toString()}`);
  };

  const handleBlockedClick = (id: string) => {
    const bt = blockedTimes.find((b) => b.id === id);
    if (bt) setSelectedBlocked(bt);
  };

  const handleDeleteBlocked = async () => {
    if (!selectedBlocked) return;
    const { error } = await supabase.from("blocked_times").delete().eq("id", selectedBlocked.id);
    if (error) {
      toast.error(error.message);
    } else {
      setBlockedTimes((prev) => prev.filter((b) => b.id !== selectedBlocked.id));
      toast.success(t("blocked.deleted"));
    }
    setConfirmDeleteBlocked(false);
    setSelectedBlocked(null);
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-border px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <h1 className="text-lg sm:text-xl font-bold text-foreground">{t("calendar.title")}</h1>
          <div className="flex items-center gap-1 rounded-lg border border-border bg-card p-0.5">
            {(["month", "week", "day"] as ViewMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`rounded-md px-2.5 sm:px-3 py-1.5 text-xs font-medium transition-colors ${
                  viewMode === mode ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {viewModeLabels[mode]}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
          <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())}>{t("calendar.today")}</Button>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigateCalendar("prev")}><ChevronLeft className="h-4 w-4" /></Button>
            <DatePickerPopover viewMode={viewMode} currentDate={currentDate} onDateChange={setCurrentDate} headerLabel={headerLabel} />
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigateCalendar("next")}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-2 sm:p-4" {...swipeHandlers}>
        {viewMode === "month" && <MonthView currentDate={currentDate} reservations={reservations} blockedTimes={blockedTimes} onReservationClick={(r) => setSelectedReservation(r)} onSlotClick={setSlotAction} onBlockedClick={handleBlockedClick} t={t} />}
        {viewMode === "week" && <WeekView currentDate={currentDate} reservations={reservations} blockedTimes={blockedTimes} openingHours={openingHours} onReservationClick={(r) => setSelectedReservation(r)} onSlotClick={setSlotAction} onBlockedClick={handleBlockedClick} />}
        {viewMode === "day" && <DayView currentDate={currentDate} reservations={reservations} blockedTimes={blockedTimes} openingHours={openingHours} onReservationClick={(r) => setSelectedReservation(r)} onSlotClick={setSlotAction} onBlockedClick={handleBlockedClick} />}
      </div>

      {/* Reservation detail dialog */}
      <Dialog open={!!selectedReservation} onOpenChange={(open) => !open && setSelectedReservation(null)}>
        <DialogContent className="sm:max-w-[420px]">
          {selectedReservation && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedReservation.service}</DialogTitle>
                <DialogDescription>
                  {selectedReservation.selectedOptions.length > 0 && selectedReservation.selectedOptions.join(", ")}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div className="flex items-center gap-3 text-sm">
                  <User className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-foreground">{selectedReservation.customerName}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-foreground">{selectedReservation.customerEmail}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-foreground">
                    {format(selectedReservation.date, "EEEE, MMMM d · HH:mm")} – {format(new Date(selectedReservation.date.getTime() + selectedReservation.duration * 60000), "HH:mm")}
                    <span className="text-muted-foreground ml-1">({selectedReservation.duration}min)</span>
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Euro className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-foreground font-medium">€{selectedReservation.price.toFixed(2)}</span>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedReservation(null)}>{t("calendar.close")}</Button>
                <Button onClick={() => { setSelectedReservation(null); navigateTo(`/dashboard/reservations/${selectedReservation.id}`); }}>
                  <ExternalLink className="h-4 w-4" />
                  {t("calendar.view_details")}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Slot quick-action dialog */}
      <SlotActionPopover
        payload={slotAction}
        onClose={() => setSlotAction(null)}
        onNewReservation={handleNewReservation}
        onBlockTime={handleBlockTime}
      />

      {/* Blocked time action dialog */}
      <Dialog open={!!selectedBlocked && !confirmDeleteBlocked} onOpenChange={(open) => { if (!open) setSelectedBlocked(null); }}>
        <DialogContent className="sm:max-w-[300px]">
          {selectedBlocked && (
            <>
              <DialogHeader>
                <DialogTitle className="text-sm font-semibold">
                  {format(new Date(selectedBlocked.start_date), "EEE d MMM")}
                  {selectedBlocked.start_time && ` · ${selectedBlocked.start_time.slice(0, 5)} - ${selectedBlocked.end_time?.slice(0, 5)}`}
                </DialogTitle>
              </DialogHeader>
              <div className="flex flex-col gap-2 pt-1">
                <Button
                  variant="outline"
                  className="justify-start gap-3 h-11"
                  onClick={() => { setSelectedBlocked(null); navigateTo(`/dashboard/blocked/${selectedBlocked.id}/edit`); }}
                >
                  <Pencil className="h-4 w-4 shrink-0" />
                  {t("blocked.edit") || "Edit"}
                </Button>
                <Button
                  variant="outline"
                  className="justify-start gap-3 h-11 text-destructive hover:text-destructive"
                  onClick={() => setConfirmDeleteBlocked(true)}
                >
                  <Trash2 className="h-4 w-4 shrink-0" />
                  {t("blocked.delete_confirm") || "Delete"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm delete blocked time */}
      <AlertDialog open={confirmDeleteBlocked} onOpenChange={(open) => { if (!open) { setConfirmDeleteBlocked(false); setSelectedBlocked(null); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("blocked.delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("blocked.delete_desc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("blocked.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteBlocked} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("blocked.delete_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

const MonthView = ({ currentDate, reservations, blockedTimes, onReservationClick, onSlotClick, onBlockedClick, t }: { currentDate: Date; reservations: CalendarReservation[]; blockedTimes: BlockedTimeEntry[]; onReservationClick: (r: CalendarReservation) => void; onSlotClick: (p: SlotClickPayload) => void; onBlockedClick: (id: string) => void; t: (key: string) => string }) => {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const days: Date[] = [];
  let day = calStart;
  while (day <= calEnd) { days.push(day); day = addDays(day, 1); }

  const dayNames = [t("calendar.mon"), t("calendar.tue"), t("calendar.wed"), t("calendar.thu"), t("calendar.fri"), t("calendar.sat"), t("calendar.sun")];

  return (
    <div className="h-full">
      <div className="grid grid-cols-7 border-b border-border">
        {dayNames.map((d) => (
          <div key={d} className="p-2 text-center text-xs font-medium text-muted-foreground">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 auto-rows-fr" style={{ minHeight: "calc(100% - 32px)" }}>
        {days.map((d) => {
          const dayReservations = reservations.filter((r) => isSameDay(r.date, d));
          const dayBlocked = isDayBlocked(d, blockedTimes);
          return (
            <div
              key={d.toISOString()}
              className={`border-b border-r border-border p-1.5 relative group/day cursor-pointer touch-manipulation ${!isSameMonth(d, currentDate) ? "bg-muted/30" : "bg-card hover:bg-muted/30"} transition-colors`}
              onClick={(e) => {
                // Only trigger if clicking the empty area (not a reservation chip)
                if ((e.target as HTMLElement).closest("[data-reservation]")) return;
                onSlotClick({ date: d });
              }}
            >
              {dayBlocked && (() => {
                const bt = findBlockedTimeForDate(d, blockedTimes);
                return (
                  <div className="absolute inset-0 bg-destructive/10 cursor-pointer z-20" style={{ backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 4px, hsl(var(--destructive) / 0.18) 4px, hsl(var(--destructive) / 0.18) 5px)' }} onClick={(e) => { e.stopPropagation(); if (bt) onBlockedClick(bt.id); }} />
                );
              })()}
              <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${isToday(d) ? "bg-primary text-primary-foreground" : !isSameMonth(d, currentDate) ? "text-muted-foreground/50" : "text-foreground"}`}>
                {format(d, "d")}
              </span>
              <div className="mt-1 space-y-0.5">
                {dayReservations.slice(0, 3).map((r) => (
                  <div data-reservation="true" key={r.id} onClick={(e) => { e.stopPropagation(); onReservationClick(r); }} className={`truncate rounded px-1.5 py-0.5 text-[10px] font-medium border ${r.color} cursor-pointer hover:opacity-80`} title={`${r.service}${r.selectedOptions.length ? ` (${r.selectedOptions.join(", ")})` : ""} · ${r.duration}min · €${r.price.toFixed(2)} · ${r.customerName} (${r.customerEmail})`}>
                    {format(r.date, "HH:mm")} {r.service}
                  </div>
                ))}
                {dayReservations.length > 3 && <span className="text-[10px] text-muted-foreground px-1.5">+{dayReservations.length - 3} {t("calendar.more")}</span>}
              </div>
              {/* Hover hint */}
              {dayReservations.length === 0 && !dayBlocked && (
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/day:opacity-100 transition-opacity pointer-events-none">
                  <span className="text-[10px] text-muted-foreground/60">+</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CurrentTimeLine = ({ startHour, rowHeight, className }: { startHour: number; rowHeight: number; className?: string }) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const interval = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(interval); }, []);
  const hours = now.getHours();
  const minutes = now.getMinutes();
  if (hours < startHour || hours >= startHour + 12) return null;
  const topPx = (hours - startHour + minutes / 60) * rowHeight;
  return (
    <div className={`absolute left-0 right-0 z-20 pointer-events-none ${className || ""}`} style={{ top: `${topPx}px` }}>
      <div className="flex items-center">
        <div className="h-2.5 w-2.5 rounded-full bg-destructive shrink-0 -ml-[5px]" />
        <div className="flex-1 border-t-2 border-destructive" />
      </div>
    </div>
  );
};

const CalendarItemTooltip = ({ r, children }: { r: CalendarReservation; children: React.ReactNode }) => {
  return (
    <div className="group/tip relative">
      {children}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 z-50 hidden group-hover/tip:flex flex-col gap-0.5 rounded-lg border border-border bg-popover px-3 py-2 shadow-lg text-popover-foreground min-w-[200px] max-w-[280px] pointer-events-none">
        <p className="text-xs font-semibold truncate">{r.service}</p>
        {r.selectedOptions.length > 0 && (
          <p className="text-[11px] opacity-80 truncate">{r.selectedOptions.join(", ")}</p>
        )}
        <p className="text-[11px] opacity-70 truncate">{r.customerName} · {r.customerEmail}</p>
        <div className="flex items-center gap-2 text-[11px] opacity-60 pt-0.5 border-t border-border mt-0.5">
          <span>{format(r.date, "HH:mm")} - {format(new Date(r.date.getTime() + r.duration * 60000), "HH:mm")}</span>
          <span>{r.duration}min</span>
          <span className="ml-auto font-medium">€{r.price.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
};

const getTimeRange = (openingHours: OpeningHourEntry[]): { startHour: number; endHour: number } => {
  const openDays = openingHours.filter((h) => h.is_open);
  if (openDays.length === 0) return { startHour: 8, endHour: 20 };
  let minHour = 24, maxHour = 0;
  openDays.forEach((h) => {
    const [oh] = h.open_time.split(":").map(Number);
    const [ch, cm] = h.close_time.split(":").map(Number);
    if (oh < minHour) minHour = oh;
    const closeHour = cm > 0 ? ch + 1 : ch;
    if (closeHour > maxHour) maxHour = closeHour;
  });
  return { startHour: minHour, endHour: maxHour };
};

const WeekView = ({ currentDate, reservations, blockedTimes, openingHours, onReservationClick, onSlotClick, onBlockedClick }: { currentDate: Date; reservations: CalendarReservation[]; blockedTimes: BlockedTimeEntry[]; openingHours: OpeningHourEntry[]; onReservationClick: (r: CalendarReservation) => void; onSlotClick: (p: SlotClickPayload) => void; onBlockedClick: (id: string) => void }) => {
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const { startHour: START_HOUR, endHour } = useMemo(() => getTimeRange(openingHours), [openingHours]);
  const slotCount = (endHour - START_HOUR) * 2;
  const slots = useMemo(() => Array.from({ length: slotCount }, (_, i) => ({ hour: Math.floor(i / 2) + START_HOUR, minute: (i % 2) * 30 })), [slotCount, START_HOUR]);
  const SLOT_HEIGHT = 40;

  return (
    <div className="h-full overflow-auto">
      <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-border sticky top-0 bg-card z-10">
        <div />
        {weekDays.map((d) => (
          <div key={d.toISOString()} className="border-l border-border p-2 text-center">
            <p className="text-xs text-muted-foreground">{format(d, "EEE")}</p>
            <p className={`text-sm font-semibold ${isToday(d) ? "text-primary" : "text-foreground"}`}>{format(d, "d")}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-[60px_repeat(7,1fr)] relative">
        {slots.map((slot, slotIdx) => (
          <div key={slotIdx} className="contents">
            <div className={`border-b border-border p-1 text-right text-[10px] text-muted-foreground flex items-start justify-end pr-2 pt-0`} style={{ height: `${SLOT_HEIGHT}px` }}>
              {slot.minute === 0 ? `${slot.hour}:00` : ""}
            </div>
            {weekDays.map((d) => {
              const slotReservations = reservations.filter((r) => isSameDay(r.date, d) && getHours(r.date) === slot.hour && getMinutes(r.date) >= slot.minute && getMinutes(r.date) < slot.minute + 30);
              const slotBlocked = isSlotBlocked(d, slot.hour, slot.minute, blockedTimes);
              const isEmpty = slotReservations.length === 0;
              return (
                <div
                  key={d.toISOString()}
                  className={`relative border-l border-border ${slot.minute === 0 ? "border-b" : "border-b border-dashed"} ${isEmpty ? "group/slot cursor-pointer" : ""} transition-colors`}
                  style={{ height: `${SLOT_HEIGHT}px` }}
                >
                  {slotBlocked && (() => {
                    const bt = findBlockedTimeForSlot(d, slot.hour, slot.minute, blockedTimes);
                    return (
                      <div className="absolute inset-0 bg-destructive/10 cursor-pointer z-20" style={{ backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 4px, hsl(var(--destructive) / 0.2) 4px, hsl(var(--destructive) / 0.2) 5px)' }} onClick={(e) => { e.stopPropagation(); if (bt) onBlockedClick(bt.id); }} />
                    );
                  })()}
                  {isEmpty && !slotBlocked && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/slot:opacity-100 transition-opacity pointer-events-none">
                      <span className="text-[10px] text-muted-foreground/50">+</span>
                    </div>
                  )}
                  {/* Tap target layer – sits on top so mobile touches always register */}
                  {isEmpty && !slotBlocked && (
                    <button
                      type="button"
                      className="absolute inset-0 z-30 touch-manipulation hover:bg-muted/40 focus:outline-none"
                      onClick={() => onSlotClick({ date: d, hour: slot.hour, minute: slot.minute })}
                      aria-label={`Add at ${String(slot.hour).padStart(2, "0")}:${String(slot.minute).padStart(2, "0")}`}
                    />
                  )}
                  {slotReservations.map((r) => {
                    const minuteOffset = getMinutes(r.date) - slot.minute;
                    const topPx = (minuteOffset / 30) * SLOT_HEIGHT;
                    return (
                      <div key={r.id} onClick={(e) => { e.stopPropagation(); onReservationClick(r); }} className={`absolute inset-x-0.5 rounded border px-1.5 text-[10px] font-medium cursor-pointer hover:opacity-80 z-10 overflow-hidden flex items-start gap-1 ${r.color}`} style={{ top: `${topPx}px`, height: `${Math.max((r.duration / 30) * SLOT_HEIGHT - 4, 16)}px`, lineHeight: '14px', paddingTop: '1px', paddingBottom: '1px' }}>
                          <span className="truncate font-semibold">{r.service}</span>
                          <span className="truncate opacity-70">{r.customerName}</span>
                          <span className="truncate opacity-70 shrink-0">{r.duration}min · €{r.price.toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        ))}
        {weekDays.map((d, colIdx) =>
          isToday(d) ? (
            <div key="now-line" className="absolute top-0 bottom-0" style={{ left: `calc(60px + (100% - 60px) / 7 * ${colIdx})`, width: `calc((100% - 60px) / 7)` }}>
              <CurrentTimeLine startHour={START_HOUR} rowHeight={SLOT_HEIGHT * 2} />
            </div>
          ) : null
        )}
      </div>
    </div>
  );
};

const DayView = ({ currentDate, reservations, blockedTimes, openingHours, onReservationClick, onSlotClick, onBlockedClick }: { currentDate: Date; reservations: CalendarReservation[]; blockedTimes: BlockedTimeEntry[]; openingHours: OpeningHourEntry[]; onReservationClick: (r: CalendarReservation) => void; onSlotClick: (p: SlotClickPayload) => void; onBlockedClick: (id: string) => void }) => {
  const { startHour: START_HOUR, endHour } = useMemo(() => getTimeRange(openingHours), [openingHours]);
  const slotCount = (endHour - START_HOUR) * 2;
  const slots = useMemo(() => Array.from({ length: slotCount }, (_, i) => ({ hour: Math.floor(i / 2) + START_HOUR, minute: (i % 2) * 30 })), [slotCount, START_HOUR]);
  const dayReservations = reservations.filter((r) => isSameDay(r.date, currentDate));
  const SLOT_HEIGHT = 48;

  return (
    <div className="h-full overflow-auto">
      <div className="grid grid-cols-[80px_1fr] relative">
        {slots.map((slot, slotIdx) => {
          const slotReservations = dayReservations.filter((r) => getHours(r.date) === slot.hour && getMinutes(r.date) >= slot.minute && getMinutes(r.date) < slot.minute + 30);
          const slotBlocked = isSlotBlocked(currentDate, slot.hour, slot.minute, blockedTimes);
          const isEmpty = slotReservations.length === 0;
          return (
            <div key={slotIdx} className="contents">
              <div className={`border-b border-border p-2 text-right text-xs text-muted-foreground flex items-start justify-end pr-3 pt-1`} style={{ height: `${SLOT_HEIGHT}px` }}>
                {slot.minute === 0 ? `${slot.hour}:00` : `${slot.hour}:30`}
              </div>
              <div
                className={`relative border-l border-border ${slot.minute === 0 ? "border-b" : "border-b border-dashed"} ${isEmpty ? "group/slot cursor-pointer" : ""} transition-colors`}
                style={{ height: `${SLOT_HEIGHT}px` }}
              >
                {slotBlocked && (() => {
                  const bt = findBlockedTimeForSlot(currentDate, slot.hour, slot.minute, blockedTimes);
                  return (
                    <div className="absolute inset-0 bg-destructive/10 cursor-pointer z-20" style={{ backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 4px, hsl(var(--destructive) / 0.2) 4px, hsl(var(--destructive) / 0.2) 5px)' }} onClick={(e) => { e.stopPropagation(); if (bt) onBlockedClick(bt.id); }} />
                  );
                })()}
                {isEmpty && !slotBlocked && (
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/slot:opacity-100 transition-opacity pointer-events-none">
                    <span className="text-xs text-muted-foreground/50 font-medium">+ Click to add</span>
                  </div>
                )}
                {/* Tap target layer – sits on top so mobile touches always register */}
                {isEmpty && !slotBlocked && (
                  <button
                    type="button"
                    className="absolute inset-0 z-30 touch-manipulation hover:bg-muted/40 focus:outline-none"
                    onClick={() => onSlotClick({ date: currentDate, hour: slot.hour, minute: slot.minute })}
                    aria-label={`Add at ${String(slot.hour).padStart(2, "0")}:${String(slot.minute).padStart(2, "0")}`}
                  />
                )}
                {slotReservations.map((r) => {
                  const minuteOffset = getMinutes(r.date) - slot.minute;
                  const topPx = (minuteOffset / 30) * SLOT_HEIGHT;
                  const itemHeight = Math.max((r.duration / 30) * SLOT_HEIGHT - 8, 20);
                  // ~16px per line; determine how many lines fit
                  const availableLines = Math.floor((itemHeight - 8) / 16);
                  return (
                    <div key={r.id} onClick={(e) => { e.stopPropagation(); onReservationClick(r); }} className={`absolute inset-x-1 rounded-lg border px-3 cursor-pointer hover:opacity-80 z-10 overflow-hidden ${r.color}`} style={{ top: `${topPx}px`, height: `${itemHeight}px`, display: 'flex', flexDirection: r.duration <= 30 ? 'row' as const : 'column' as const, alignItems: r.duration <= 30 ? 'center' : 'flex-start', gap: r.duration <= 30 ? '6px' : '0', paddingTop: r.duration <= 30 ? '2px' : '4px', paddingBottom: r.duration <= 30 ? '2px' : '4px' }}>
                        {r.duration <= 30 ? (
                          <>
                            <span className="text-xs font-semibold truncate">{r.service}</span>
                            <span className="text-[10px] opacity-80 truncate">{r.customerName}</span>
                            <span className="text-[10px] opacity-60 truncate shrink-0">{format(r.date, "HH:mm")} · {r.duration}min · €{r.price.toFixed(2)}</span>
                          </>
                        ) : (
                          <>
                            <p className="text-xs font-semibold truncate w-full leading-4">{r.service}{r.selectedOptions.length > 0 ? ` (${r.selectedOptions.join(", ")})` : ""}</p>
                            {availableLines >= 2 && <p className="text-[11px] opacity-70 truncate w-full leading-4">{r.customerName} · {r.customerEmail}</p>}
                            {availableLines >= 3 && <p className="text-[11px] opacity-60 truncate w-full leading-4">{format(r.date, "HH:mm")} - {format(new Date(r.date.getTime() + r.duration * 60000), "HH:mm")} · {r.duration}min · €{r.price.toFixed(2)}</p>}
                          </>
                        )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        {isToday(currentDate) && (
          <div className="absolute top-0 bottom-0" style={{ left: "80px", right: 0 }}>
            <CurrentTimeLine startHour={START_HOUR} rowHeight={SLOT_HEIGHT * 2} />
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarPage;
